import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

function PlanModal({ plan: editing, onClose, onSuccess, t }) {
  const isEdit = !!editing;
  const [form, setForm] = useState({
    name:        editing?.name        || '',
    price:       editing?.price       ?? '',
    active:      editing?.active      ?? true,
  });
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handle = async (e) => {
    e.preventDefault(); setError('');
    setLoading(true);
    try {
      const payload = { name: form.name, price: Number(form.price), active: form.active };
      if (isEdit) await axios.put(`/api/plans/${editing._id}`, payload);
      else        await axios.post('/api/plans', payload);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving plan');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <h2 style={{ fontSize: 22 }}>{isEdit ? t('editPlan') : t('createPlan')}</h2>
          <button onClick={onClose} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>
        <form onSubmit={handle}>
          <div className="form-group">
            <label className="form-label">{t('planName')} *</label>
            <input type="text" required placeholder="e.g. Basic, Pro, Enterprise"
              value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('planPrice')} *</label>
            <input type="number" required min="0" placeholder="500000"
              value={form.price} onChange={e => set('price', e.target.value)} />
          </div>
          {isEdit && (
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: 'var(--accent)', flexShrink: 0 }} />
                <span style={{ fontSize: 14 }}>{t('planActive')}</span>
              </label>
            </div>
          )}
          {error && (
            <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 14, border: '1px solid rgba(224,92,92,0.2)' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1 }}>{t('cancel')}</button>
            <button type="submit" className="btn-primary" style={{ flex: 2 }} disabled={loading}>
              {loading ? t('saving') : t('save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Plans() {
  const { t } = useSettings();
  const [plans,   setPlans]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | 'create' | planObj

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/plans/all');
      setPlans(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try { await axios.delete(`/api/plans/${plan._id}`); fetchPlans(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const totalRevenue = null; // could be calculated from subscriptions if needed

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 34, marginBottom: 6 }}>{t('planManagement')}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            {plans.filter(p => p.active).length} active · {plans.filter(p => !p.active).length} inactive
          </p>
        </div>
        <button className="btn-primary" onClick={() => setModal('create')}>+ {t('createPlan')}</button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>
      ) : plans.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📋</div>
          <div style={{ color: 'var(--text-muted)', marginBottom: 16 }}>{t('noPlans')}</div>
          <button className="btn-primary" onClick={() => setModal('create')}>+ {t('createPlan')}</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {plans.map(plan => (
            <div key={plan._id} className="card" style={{
              opacity: plan.active ? 1 : 0.55,
              border: `1px solid ${plan.active ? 'var(--border)' : 'var(--border)'}`,
              position: 'relative', overflow: 'hidden',
            }}>
              {/* Active indicator stripe */}
              <div style={{
                position: 'absolute', top: 0, left: 0, width: 4, height: '100%',
                background: plan.active ? 'var(--accent)' : 'var(--text-dim)',
              }} />

              <div style={{ paddingLeft: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2 }}>{plan.name}</div>
                    {!plan.active && (
                      <span className="badge badge-gray" style={{ fontSize: 10 }}>Inactive</span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'DM Serif Display', fontSize: 22, color: 'var(--accent)' }}>
                      {plan.price.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>UZS / mo</div>
                  </div>
                </div>

                <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 14 }}>
                  Created {new Date(plan.createdAt).toLocaleDateString('en-GB')}
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-ghost" style={{ fontSize: 12, padding: '6px 12px', flex: 1 }}
                    onClick={() => setModal(plan)}>
                    {t('editPlan')}
                  </button>
                  <button className="btn-danger" style={{ fontSize: 12, padding: '6px 12px' }}
                    onClick={() => handleDelete(plan)}>
                    {t('delete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <PlanModal
          plan={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSuccess={() => { setModal(null); fetchPlans(); }}
          t={t}
        />
      )}
    </div>
  );
}
